import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { AuditProgram } from '../audit.models';
import { AuditService } from '../audit.service';
import {
  auditNavItems,
  auditTypeOptions,
  frequencyOptions,
  moduleOptions,
  riskOptions,
  scopeTypeOptions,
  statusSeverity
} from '../audit-ui';

@Component({
  selector: 'app-audit-programs',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    DialogModule,
    IftaLabelModule,
    InputGroupAddonModule,
    InputGroupModule,
    InputNumberModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    ToggleSwitchModule,
    TooltipModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService],
  template: `
    <p-toast></p-toast>
    @if (loading) {
      <p-progressBar mode="indeterminate"></p-progressBar>
    }

    <app-page-shell>
      <app-page-hero eyebrow="Audit Setup" title="Audit Programs" subtitle="Tenant audit programs by module, scope, frequency, and risk.">
        <div nav>
          @for (item of visibleNavItems; track item.route) {
            <a pButton [routerLink]="item.route" [label]="item.label" [icon]="item.icon" [outlined]="item.route !== '/audit/programs'" severity="secondary"></a>
          }
        </div>
        <div actions>
          <p-button label="Refresh" icon="pi pi-refresh" [outlined]="true" severity="secondary" (onClick)="load()"></p-button>
          <p-button label="New Program" icon="pi pi-plus" (onClick)="openProgram()" [disabled]="!canManage"></p-button>
        </div>
      </app-page-hero>

      <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

      <app-page-data-panel title="Programs" subtitle="Reusable audit program headers for all LogiLink areas." [resultText]="programs.length + ' programs'">
        <p-table [value]="programs" [loading]="loading" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '960px' }">
          <ng-template pTemplate="header">
            <tr>
              <th>Program</th>
              <th>Module</th>
              <th>Type</th>
              <th>Scope</th>
              <th>Frequency</th>
              <th>Risk</th>
              <th>Status</th>
              <th class="text-right">Actions</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-program>
            <tr>
              <td>
                <div class="audit-stack">
                  <strong>{{ program.programName }}</strong>
                  <small>{{ program.programCode }}</small>
                </div>
              </td>
              <td>{{ program.moduleCode || '-' }}</td>
              <td>{{ program.auditType || '-' }}</td>
              <td>{{ program.scopeType || '-' }}</td>
              <td>{{ program.frequency || '-' }}</td>
              <td><p-tag [value]="program.riskLevel || '-'" [severity]="statusSeverity(program.riskLevel)"></p-tag></td>
              <td><p-tag [value]="program.active === false ? 'INACTIVE' : 'ACTIVE'" [severity]="program.active === false ? 'secondary' : 'success'"></p-tag></td>
              <td class="text-right">
                <p-button icon="pi pi-pencil" [text]="true" pTooltip="Edit" (onClick)="openProgram(program)" [disabled]="!canManage"></p-button>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="8" class="text-center p-4">No audit programs are configured.</td></tr>
          </ng-template>
        </p-table>
      </app-page-data-panel>
    </app-page-shell>

    <p-dialog
      [header]="form.auditProgramId ? 'Edit Audit Program' : 'New Audit Program'"
      [(visible)]="dialogVisible"
      [modal]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: 'min(920px, 96vw)' }">
      <div class="audit-form-grid">
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-tag"></i></p-inputgroup-addon>
          <p-iftalabel>
            <input pInputText id="program-code" [(ngModel)]="form.programCode">
            <label for="program-code">Program code</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-id-card"></i></p-inputgroup-addon>
          <p-iftalabel>
            <input pInputText id="program-name" [(ngModel)]="form.programName">
            <label for="program-name">Program name</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-th-large"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-select inputId="program-module" [options]="moduleOptions" [(ngModel)]="form.moduleCode" optionLabel="label" optionValue="value" [showClear]="true"></p-select>
            <label for="program-module">Module</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-verified"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-select inputId="program-audit-type" [options]="auditTypeOptions" [(ngModel)]="form.auditType" optionLabel="label" optionValue="value" [showClear]="true"></p-select>
            <label for="program-audit-type">Audit type</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-crosshairs"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-select inputId="program-scope" [options]="scopeTypeOptions" [(ngModel)]="form.scopeType" optionLabel="label" optionValue="value" [showClear]="true"></p-select>
            <label for="program-scope">Scope</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-calendar"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-select inputId="program-frequency" [options]="frequencyOptions" [(ngModel)]="form.frequency" optionLabel="label" optionValue="value" [showClear]="true"></p-select>
            <label for="program-frequency">Frequency</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-flag"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-select inputId="program-risk" [options]="riskOptions" [(ngModel)]="form.riskLevel" optionLabel="label" optionValue="value" [showClear]="true"></p-select>
            <label for="program-risk">Risk level</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-clock"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-inputnumber inputId="program-default-due-days" [(ngModel)]="form.defaultDueDays" [min]="0" [showButtons]="true"></p-inputnumber>
            <label for="program-default-due-days">Default due days</label>
          </p-iftalabel>
        </p-inputgroup>
        <label class="audit-switch-line">
          <p-toggleswitch [(ngModel)]="form.active"></p-toggleswitch>
          <span>Active</span>
        </label>
      </div>
      <p-inputgroup class="audit-textarea-field">
        <p-inputgroup-addon><i class="pi pi-align-left"></i></p-inputgroup-addon>
        <p-iftalabel>
          <textarea pTextarea id="program-description" rows="4" [(ngModel)]="form.description"></textarea>
          <label for="program-description">Description</label>
        </p-iftalabel>
      </p-inputgroup>
      @if (formError) {
        <div class="audit-error">{{ formError }}</div>
      }
      <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="dialogVisible = false"></p-button>
        <p-button label="Save Program" icon="pi pi-check" (onClick)="saveProgram()" [loading]="saving" [disabled]="!canManage"></p-button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .audit-stack {
      display: flex;
      flex-direction: column;
      gap: .25rem;
      min-width: 0;
    }

    .audit-stack small {
      color: #64748b;
      font-size: .78rem;
    }

    .audit-form-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(180px, 1fr));
      gap: .85rem;
    }

    label,
    .audit-textarea-field {
      color: #334155;
      display: grid;
      gap: .35rem;
      min-width: 0;
    }

    label span,
    .audit-textarea-field span {
      color: #64748b;
      font-size: .82rem;
      font-weight: 700;
    }

    input,
    textarea,
    p-iftalabel,
    p-inputgroup,
    p-select,
    p-inputnumber {
      width: 100%;
    }

    .audit-switch-line {
      align-items: center;
      display: flex;
      gap: .65rem;
      padding-top: 1.55rem;
    }

    .audit-textarea-field {
      margin-top: .85rem;
    }

    .audit-error {
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 8px;
      color: #991b1b;
      font-weight: 700;
      margin-top: .85rem;
      padding: .75rem;
    }

    @media (max-width: 900px) {
      .audit-form-grid {
        grid-template-columns: 1fr;
      }
    }
  `]
})
export class AuditProgramsComponent implements OnInit {
  private readonly auditService = inject(AuditService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);

  loading = false;
  saving = false;
  dialogVisible = false;
  formError = '';
  programs: AuditProgram[] = [];
  form: AuditProgram = {};

  readonly moduleOptions = moduleOptions;
  readonly auditTypeOptions = auditTypeOptions;
  readonly scopeTypeOptions = scopeTypeOptions;
  readonly frequencyOptions = frequencyOptions;
  readonly riskOptions = riskOptions;
  readonly visibleNavItems = auditNavItems.filter(item => this.permissionService.hasPermission(item.permission));
  readonly canManage = this.permissionService.hasPermission(app_permissions.audit.programs.manage);
  readonly statusSeverity = statusSeverity;

  ngOnInit(): void {
    this.load();
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Programs', value: this.programs.length, tone: 'info' },
      { label: 'Active', value: this.programs.filter(program => program.active !== false).length, tone: 'success' },
      { label: 'High Risk', value: this.programs.filter(program => ['HIGH', 'CRITICAL'].includes(program.riskLevel || '')).length, tone: 'warn' },
      { label: 'Modules', value: new Set(this.programs.map(program => program.moduleCode).filter(Boolean)).size }
    ];
  }

  load(): void {
    this.loading = true;
    this.auditService.programs().subscribe({
      next: response => this.programs = response.data ?? [],
      error: () => this.messageService.add({ severity: 'error', summary: 'Programs', detail: 'Unable to load audit programs.' }),
      complete: () => this.loading = false
    });
  }

  openProgram(program?: AuditProgram): void {
    this.formError = '';
    this.form = program ? { ...program } : { active: true };
    this.dialogVisible = true;
  }

  saveProgram(): void {
    this.formError = '';
    if (!this.form.programCode?.trim() || !this.form.programName?.trim()) {
      this.formError = 'Program code and program name are required.';
      return;
    }
    this.saving = true;
    this.auditService.saveProgram(this.form).subscribe({
      next: response => {
        this.messageService.add({ severity: 'success', summary: 'Program saved', detail: response.message || 'Audit program saved.' });
        this.dialogVisible = false;
        this.load();
      },
      error: error => this.formError = error?.error?.message || 'Unable to save audit program.',
      complete: () => this.saving = false
    });
  }
}
